import { Form, FormInstance } from 'antd';
import type { SessionForm } from '@components/Clinicians/session-form';
import moment from 'moment';

import { ACTION_BKVCON_CODE, ACTION_COMMENT_KEY } from '@components/Clinicians/session-form';

import { useSessionFormState } from './useSessionFormState';
import { useSessionOutcome } from './useSessionOutcome';
import { ACTION_KEYS } from '../components/Clinicians/Session/v2/Actions';

const getSessionFieldValue = <Key extends keyof SessionForm>(
  form: FormInstance<SessionForm>,
  key: Key
): SessionForm[Key] => {
  return form.getFieldValue(key);
};

export const useSessionAppointmentActions = () => {
  const form = Form.useFormInstance<SessionForm>();
  const [appointmentActions = []] = useSessionFormState('appt_actions');

  const { appointmentVersion, availableActions } = useSessionOutcome();

  if (appointmentVersion !== 2) return [];

  return appointmentActions
    .map(({ appt_action_code }) => {
      if (!form) return { appt_action_code };
      const action = availableActions.find((action) => action.appt_action_code === appt_action_code);

      const subtype =
        action?.action_subtypes?.find(
          (subtype) => subtype.action_subtype_code === getSessionFieldValue(form, `action_subtype_${appt_action_code}`)
        ) ??
        action?.action_subtypes?.find(
          (subtype) =>
            subtype.action_subtype_code ===
            getSessionFieldValue(form, `appt_action_${appt_action_code}`)?.action_subtype_code
        );

      if (!subtype && appt_action_code !== ACTION_BKVCON_CODE) {
        return { appt_action_code };
      }

      if (appt_action_code in ACTION_KEYS) {
        const actionKey = ACTION_KEYS[appt_action_code];
        if (actionKey) return { ...getSessionFieldValue(form, actionKey), appt_action_code };
      }
      const action_task_comment = getSessionFieldValue(form, `${ACTION_COMMENT_KEY}_${appt_action_code}`) || undefined;

      if (subtype?.auth_enabled) {
        const hasAuth = getSessionFieldValue(form, `auth_boolean_${appt_action_code}`);

        const [auth_start_date, auth_end_date] = hasAuth
          ? ([`auth_start_date_${appt_action_code}`, `auth_end_date_${appt_action_code}`] as const).map((key) => {
              const date = getSessionFieldValue(form, key);

              return date ? moment(date).format('YYYY-MM-DD') : undefined;
            })
          : [undefined, undefined];

        return {
          appt_action_code,
          action_subtype_code: subtype.action_subtype_code,
          auth_granted: Number(hasAuth),
          auth_start_date,
          auth_end_date,
          action_task_comment
        };
      }

      return {
        appt_action_code,
        action_subtype_code: subtype?.action_subtype_code,
        action_task_comment
      };
    })
    .filter((action, index, self) => index === self.findIndex((t) => t.appt_action_code === action.appt_action_code));
};
